import { showGlobalToast } from '../context/ToastContext';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

class ApiClient {
  constructor() {
    this.currentAiAgentId = localStorage.getItem('activeAiAgentId') || null;
  }

  setAiAgentId(id) {
    if (id) {
      this.currentAiAgentId = id.toString();
      localStorage.setItem('activeAiAgentId', this.currentAiAgentId);
    }
  }

  getAuthToken() {
    const user = sessionStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      return userData.token;
    }
    return null;
  }

  get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  post(endpoint, body = {}, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  put(endpoint, body = {}, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  async request(endpoint, options = {}) {
    const cleanEndpoint = (endpoint.startsWith('/api') && API_BASE_URL.endsWith('/api'))
      ? endpoint.substring(4)
      : endpoint;
    const url = `${API_BASE_URL}${cleanEndpoint}`;
    const token = this.getAuthToken();

    // Use the explicitly set aiAgentId, or fallback to what's in options/localStorage
    const aiAgentId = options.aiAgentId || this.currentAiAgentId || localStorage.getItem('activeAiAgentId');

    const activeTenantId = localStorage.getItem('activeTenantId');

    const isFormData = options.body instanceof FormData;

    // Normalize headers to prevent duplication
    const normalizedHeaders = {
      ...(!isFormData && { 'Content-Type': 'application/json' }),
      'X-AI-Agent-ID': aiAgentId,
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(activeTenantId && { 'X-Tenant-ID': activeTenantId }),
    };

    // Merge options.headers while removing any existing agent ID keys to prevent comma-concatenation
    if (options.headers) {
      Object.keys(options.headers).forEach(key => {
        const lowerKey = key.toLowerCase();
        if (lowerKey !== 'x-ai-agent-id') {
          normalizedHeaders[key] = options.headers[key];
        }
      });
    }

    const config = {
      ...options,
      headers: normalizedHeaders
    };
    try {
      const response = await fetch(url, config);
      if (response.status === 401) {
        if (cleanEndpoint !== '/auth/login' && cleanEndpoint !== '/api/auth/login') {
          // Auto logout on unauthorized — clear ALL state
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = '/login';
          throw new Error('Session expired. Please login again.');
        }
      }
      if (!response.ok) {
        const status = response.status;
        let errorMessage = "An unexpected error occurred.";

        try {
          const errorData = await response.json();
          if (errorData.detail) {
            const detail = errorData.detail;
            if (Array.isArray(detail)) {
              const messages = detail.map(err => {
                const path = err.loc ? err.loc.filter(l => l !== 'body').join('.') : '';
                return path ? `${path}: ${err.msg}` : err.msg;
              });
              errorMessage = messages.join(' | ');
            } else {
              errorMessage = typeof detail === 'object' ? JSON.stringify(detail) : detail;
            }
          }
        } catch (parseError) {
          errorMessage = `HTTP error! status: ${status}`;
        }

        // Handle each status code explicitly with descriptive messages
        const shouldSkipToast = Boolean(
          options.skipToast ||
          options.silent ||
          cleanEndpoint === '/auth/login' ||
          cleanEndpoint === '/api/auth/login'
        );

        if (!shouldSkipToast) {
          if (status === 400) {
            showGlobalToast(`Bad Request: ${errorMessage}`, 'error');
          } else if (status === 403) {
            showGlobalToast(`Access Denied: ${errorMessage}`, 'error');
          } else if (status === 404) {
            showGlobalToast(`Resource Not Found: ${errorMessage}`, 'error');
          } else if (status === 409) {
            showGlobalToast(`Conflict: ${errorMessage}`, 'error');
          } else if (status === 422) {
            showGlobalToast(`Validation Error: ${errorMessage}`, 'error');
          } else if (status === 429) {
            showGlobalToast('Too Many Requests: Rate limit exceeded. Please try again later.', 'error');
          } else if (status === 502) {
            showGlobalToast('Bad Gateway: Server received an invalid response from upstream.', 'error');
          } else if (status === 503) {
            showGlobalToast('Service Unavailable: The server is temporarily overloaded or down.', 'error');
          } else if (status === 504) {
            showGlobalToast('Gateway Timeout: The server took too long to respond.', 'error');
          } else if (status >= 500) {
            showGlobalToast('Internal Server Error: Processing failed on the server.', 'error');
          } else {
            showGlobalToast(errorMessage, 'error');
          }
        }

        throw new Error(errorMessage);
      }
      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      // Toast is already displayed for status-code errors above.
      // We only show a generic toast here if it's a network disconnection/fetch failure.
      if (error.message && error.message.includes('Failed to fetch')) {
        showGlobalToast('Network Error: Please check your internet connection.', 'error');
      }
      throw error;
    }
  }

  // Logo APIs
  async uploadLogo(file) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request('/logo/upload', {
      method: 'PUT',
      body: formData,
    });
  }

  async uploadWidgetIcon(file) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request('/widget/icon/upload', {
      method: 'POST',
      body: formData,
    });
  }

  async getCurrentLogo() {
    const data = await this.request('/logo/config');
    if (data && data.logo_url) {
      if (data.isDefault) {
        data.logo_url = '/default.png';
      } else if (data.logo_url.startsWith('/uploads') || data.logo_url.startsWith('/logo/')) {
        data.logo_url = `${API_BASE_URL}${data.logo_url}`;
      }
    }
    return data;
  }

  // Document APIs
  async uploadDocuments(files, metadata = {}) {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    if (Object.keys(metadata).length > 0) {
      formData.append('metadata', JSON.stringify(metadata));
    }

    return this.request('/documents/upload', {
      method: 'POST',
      body: formData,
    });
  }

  async getDocuments() {
    return this.request('/documents/all');
  }

  async uploadZipRepository(repository, zipFile) {
    const formData = new FormData();
    formData.append('repository', repository);
    formData.append('zip_file', zipFile);

    return this.request('/documents/upload-zip', {
      method: 'POST',
      body: formData,
    });
  }

  async getRepositories() {
    return this.request('/documents/repositories');
  }

  async getFolderDocuments(repository = null) {
    const query = repository ? `?repository=${encodeURIComponent(repository)}` : '';
    return this.request(`/documents/folders${query}`);
  }

  async getDocumentChunks(docId) {
    return this.request(`/documents/${docId}/chunks`);
  }

  async getKnowledgeStats() {
    return this.request('/documents/all').then(data => {
      const docs = data.documents || [];
      return {
        total_documents: docs.length,
        processed_documents: docs.filter(d => d.status === 'completed').length,
        total_size_bytes: docs.reduce((acc, d) => acc + (d.file_size || 0), 0)
      };
    });
  }

  async deleteDocument(docId) {
    return this.request(`/documents/${docId}`, { method: 'DELETE' });
  }

  async downloadDocument(docId, filename) {
    const url = `${API_BASE_URL}/documents/download/${docId}`;
    const token = this.getAuthToken();
    const aiAgentId = this.currentAiAgentId;
    const activeTenantId = localStorage.getItem('activeTenantId');

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'X-AI-Agent-ID': aiAgentId,
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...(activeTenantId && { 'X-Tenant-ID': activeTenantId }),
      }
    });

    if (!response.ok) {
      throw new Error('Failed to download document');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  }

  async processDocument(docId) {
    return this.request(`/documents/process/${docId}`, { method: 'POST' });
  }

  async getDocumentStatus(docId) {
    return this.request(`/documents/status/${docId}`);
  }



  // Widget Access APIs (for Admin/Agent dashboards)
  async getWidgetSessions(aiAgentId, page = 1, limit = 10, escalated = false) {
    return this.request(`/widget/sessions?ai_agent_id=${aiAgentId}&page=${page}&limit=${limit}&escalated=${escalated}`, {
      aiAgentId
    });
  }

  async getWidgetHistory(sessionId, aiAgentId, includePrivateNotes = false) {
    return this.request(`/widget/chat/history/${sessionId}?ai_agent_id=${aiAgentId}&include_private_notes=${includePrivateNotes}`, {
      aiAgentId
    });
  }

  async deleteWidgetSession(sessionId, aiAgentId) {
    return this.request(`/widget/sessions/${sessionId}?ai_agent_id=${aiAgentId}`, {
      method: 'DELETE',
      aiAgentId
    });
  }

  // Widget Admin APIs
  async getContacts(page = 1, limit = 10, search = '', channel = '') {
    let url = `/widget/admin/contacts?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`;
    if (channel) {
      url += `&channel=${encodeURIComponent(channel)}`;
    }
    return this.request(url);
  }

  async getContact(contactId) {
    return this.request(`/widget/admin/contacts/${contactId}`);
  }

  async getGlobalCustomFields() {
    return this.request('/widget/admin/contacts/fields');
  }

  async updateGlobalCustomFields(fields) {
    return this.request('/widget/admin/contacts/fields', {
      method: 'PUT',
      body: JSON.stringify({ fields }),
    });
  }

  async updateContact(contactId, data) {
    return this.request(`/widget/admin/contacts/${contactId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async uploadContactAvatar(contactId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request(`/widget/admin/contacts/${contactId}/avatar`, {
      method: 'POST',
      body: formData,
    });
  }

  async deleteContact(contactId) {
    return this.request(`/widget/admin/contacts/${contactId}`, {
      method: 'DELETE',
    });
  }

  async getEscalations() {
    return this.request('/widget/admin/escalations');
  }

  async takeOverSession(sessionId) {
    return this.request(`/widget/sessions/${sessionId}/takeover`, {
      method: 'POST',
    });
  }


  async getWidgetConfig() {
    return this.request('/widget/admin/config');
  }

  async updateWidgetConfig(config) {
    return this.request('/widget/admin/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async patchWidgetConfig(config) {
    return this.request('/widget/admin/config', {
      method: 'PATCH',
      body: JSON.stringify(config),
    });
  }







  // API Tools APIs
  async getApiTools() {
    return this.request('/tools');
  }

  async createApiTool(tool) {
    return this.request('/tools', {
      method: 'POST',
      body: JSON.stringify(tool),
    });
  }

  async updateApiTool(toolId, tool) {
    return this.request(`/tools/${toolId}`, {
      method: 'PUT',
      body: JSON.stringify(tool),
    });
  }

  async toggleApiTool(toolId, isActive) {
    return this.request(`/tools/${toolId}`, {
      method: 'PUT',
      body: JSON.stringify({ is_active: isActive }),
    });
  }

  async deleteApiTool(toolId) {
    return this.request(`/tools/${toolId}`, {
      method: 'DELETE',
    });
  }

  async testApiTool(toolId, inputs) {
    return this.request(`/tools/${toolId}/test`, {
      method: 'POST',
      body: JSON.stringify(inputs),
    });
  }

  // Aliases for compatibility
  getTools() { return this.getApiTools(); }
  createTool(tool) { return this.createApiTool(tool); }
  updateTool(toolId, tool) { return this.updateApiTool(toolId, tool); }
  deleteTool(toolId) { return this.deleteApiTool(toolId); }
  toggleTool(toolId, isActive) { return this.toggleApiTool(toolId, isActive); }
  testTool(toolId, inputs) { return this.testApiTool(toolId, inputs); }

  // Domain Management APIs
  async getAiAgents() {
    return this.request('/ai-agents');
  }

  async getHumanAgents() {
    return this.request('/human-agents');
  }

  async getDomains() {
    return this.request('/domains');
  }

  async addDomain(domain) {
    return this.request('/domains', {
      method: 'POST',
      body: JSON.stringify({ domain }),
    });
  }

  async deleteDomain(domainId) {
    return this.request(`/domains/${domainId}`, {
      method: 'DELETE',
    });
  }

  // Agent APIs
  async getAgentSessions() {
    return this.request('/human-agent-chat/sessions');
  }

  async getAgentMessages(sessionId) {
    return this.request(`/human-agent-chat/messages/${sessionId}`);
  }

  async sendAgentMessage(sessionId, message) {
    return this.request('/human-agent-chat/send', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId, message }),
    });
  }

  async closeAgentSession(sessionId) {
    return this.request(`/human-agent-chat/close/${sessionId}`, {
      method: 'POST',
    });
  }

  // Knowledge Base (Smart FAQ) APIs
  async getKBTopics() {
    return this.request('/knowledge-base/topics');
  }

  async createKBTopic(name, description) {
    return this.request('/knowledge-base/topics', {
      method: 'POST',
      body: JSON.stringify({ name, description }),
    });
  }

  async deleteKBTopic(topicId) {
    return this.request(`/knowledge-base/topics/${topicId}`, {
      method: 'DELETE',
    });
  }

  async getKBQuestions(topicId) {
    return this.request(`/knowledge-base/questions/${topicId}`);
  }

  async createKBQuestion(topicId, text) {
    return this.request('/knowledge-base/questions', {
      method: 'POST',
      body: JSON.stringify({ topic_id: topicId, text }),
    });
  }

  async deleteKBQuestion(questionId) {
    return this.request(`/knowledge-base/questions/${questionId}`, {
      method: 'DELETE',
    });
  }

  async createKBResponse(questionId, instructionText, triggerCondition) {
    return this.request('/knowledge-base/responses', {
      method: 'POST',
      body: JSON.stringify({
        question_id: questionId,
        instruction_text: instructionText,
        trigger_condition: triggerCondition
      }),
    });
  }

  async deleteKBResponse(responseId) {
    return this.request(`/knowledge-base/responses/${responseId}`, {
      method: 'DELETE',
    });
  }

  async updateKBQuestion(questionId, text) {
    return this.request(`/knowledge-base/questions/${questionId}`, {
      method: 'PUT',
      body: JSON.stringify({ text }),
    });
  }

  async updateKBResponse(responseId, instructionText, triggerCondition) {
    return this.request(`/knowledge-base/responses/${responseId}`, {
      method: 'PUT',
      body: JSON.stringify({
        instruction_text: instructionText,
        trigger_condition: triggerCondition
      }),
    });
  }

  async generateFAQVariations(topic, question) {
    return this.request('/knowledge-base/generate-faq', {
      method: 'POST',
      body: JSON.stringify({ topic, question }),
    });
  }

  async getInstructions(channel = 'webchat') {
    return this.request(`/instructions?channel=${encodeURIComponent(channel)}`);
  }

  async getInstructionVersions(channel = 'webchat') {
    return this.request(`/instructions/versions?channel=${encodeURIComponent(channel)}`);
  }

  async getActiveInstructionChannels() {
    return this.request('/instructions/active-channels');
  }

  async createInstructions(content, versionName, channel = 'webchat', guardrailsEnabled = false, guardrailsContent = '', afterHoursEnabled = false, afterHoursInstruction = '') {
    return this.request('/instructions', {
      method: 'POST',
      body: JSON.stringify({
        content,
        version_name: versionName,
        channel,
        guardrails_enabled: guardrailsEnabled,
        guardrails_content: guardrailsContent,
        after_hours_enabled: afterHoursEnabled,
        after_hours_instruction: afterHoursInstruction
      }),
    });
  }

  async updateGuardrails(channel = 'webchat', guardrailsEnabled = false, guardrailsContent = '') {
    return this.request('/instructions/guardrails', {
      method: 'PUT',
      body: JSON.stringify({
        channel,
        guardrails_enabled: guardrailsEnabled,
        guardrails_content: guardrailsContent
      }),
    });
  }

  async updateAfterHoursInstruction(channel = 'webchat', afterHoursEnabled = false, afterHoursInstruction = '') {
    return this.request('/instructions/after-hours', {
      method: 'PUT',
      body: JSON.stringify({
        channel,
        after_hours_enabled: afterHoursEnabled,
        after_hours_instruction: afterHoursInstruction
      }),
    });
  }

  async activateInstruction(instructionId) {
    return this.request(`/instructions/activate/${instructionId}`, {
      method: 'POST',
    });
  }

  async deleteInstruction(instructionId) {
    return this.request(`/instructions/${instructionId}`, {
      method: 'DELETE',
    });
  }

  async improvePrompt(prompt) {
    return this.request('/instructions/improve', {
      method: 'POST',
      body: JSON.stringify({ prompt }),
    });
  }

  // Campaign APIs
  async getCampaigns() {
    return this.request('/campaigns');
  }

  async createCampaign(campaign) {
    // Split priority from the rest of the campaign data as expected by backend
    const { priority, ...data } = campaign;
    return this.request('/campaigns', {
      method: 'POST',
      body: JSON.stringify({
        priority: priority || 0,
        data: data
      }),
    });
  }

  async updateCampaign(id, campaign) {
    // Handle partial updates by extracting top-level fields
    const { priority, is_active, ...data } = campaign;
    const payload = {};

    if (priority !== undefined) payload.priority = priority;
    if (is_active !== undefined) payload.is_active = is_active;

    // Only include data if there are other fields besides priority/is_active
    if (Object.keys(data).length > 0) {
      payload.data = data;
    }

    return this.request(`/campaigns/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteCampaign(id) {
    return this.request(`/campaigns/${id}`, {
      method: 'DELETE',
    });
  }

  async getLiveCampaign() {
    return this.request('/campaigns/live');
  }



  // Business Flow APIs
  async getFlows() {
    return this.request('/flows');
  }

  async saveFlow(flowData) {
    return this.request('/flows', {
      method: 'POST',
      body: JSON.stringify(flowData),
    });
  }

  async updateFlow(flowId, flowData) {
    return this.request(`/flows/${flowId}`, {
      method: 'PUT',
      body: JSON.stringify(flowData),
    });
  }

  async deleteFlow(flowId) {
    return this.request(`/flows/${flowId}`, {
      method: 'DELETE',
    });
  }

  async getFlowDetail(flowId) {
    return this.request(`/flows/${flowId}`);
  }

  // Bot Flow APIs
  async getBotFlows() {
    return this.request('/bot-flows');
  }

  async saveBotFlow(flowData) {
    return this.request('/bot-flows', {
      method: 'POST',
      body: JSON.stringify(flowData),
    });
  }

  async updateBotFlow(flowId, flowData) {
    return this.request(`/bot-flows/${flowId}`, {
      method: 'PUT',
      body: JSON.stringify(flowData),
    });
  }

  async deleteBotFlow(flowId) {
    return this.request(`/bot-flows/${flowId}`, {
      method: 'DELETE',
    });
  }

  async getBotFlowDetail(flowId) {
    return this.request(`/bot-flows/${flowId}`);
  }

  async toggleBotFlowActive(flowId, isActive) {
    return this.request(`/bot-flows/${flowId}/toggle-active?is_active=${isActive}`, {
      method: 'PUT',
    });
  }

  async generateBotFlow(scenario) {
    return this.post('/bot-flows/generate', { scenario });
  }

  async sendPrivateNote(sessionId, message, aiAgentId) {
    return this.post('/widget/chat/private-note', {
      session_id: sessionId,
      message: message
    }, { aiAgentId });
  }

  async requestHandoff(sessionId, reason = 'User requested human assistant via bot flow', integrationTarget = null) {
    const payload = {
      session_id: sessionId,
      reason,
    };
    if (integrationTarget) {
      payload.integration_target = integrationTarget;
    }
    return this.post('/widget/handoff/request', payload);
  }

  // Segment Management APIs
  async getSegments() {
    return this.request('/widget/admin/segments');
  }

  async createSegment(data) {
    return this.request('/widget/admin/segments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSegment(segmentId, data) {
    return this.request(`/widget/admin/segments/${segmentId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSegment(segmentId) {
    return this.request(`/widget/admin/segments/${segmentId}`, {
      method: 'DELETE',
    });
  }

  async getWhatsAppCampaigns() {
    return this.request('/whatsapp/campaigns');
  }

  async createWhatsAppCampaign(data) {
    return this.request('/whatsapp/campaigns', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteWhatsAppCampaign(campaignId) {
    return this.request(`/whatsapp/campaigns/${campaignId}`, {
      method: 'DELETE',
    });
  }

  async getWhatsAppCampaignMessages(campaignId) {
    return this.request(`/whatsapp/campaigns/${campaignId}/messages`);
  }

  async getWhatsAppTemplates(options = {}) {
    return this.request('/whatsapp/templates', options);
  }

}

const apiClient = new ApiClient();
export default apiClient;